import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  TuiButtonModule,
  TuiDialogService,
  TuiHintModule,
  TuiSvgModule,
} from '@taiga-ui/core';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { AlertService } from '@shared/services/alert.service';
import { AsyncPipe } from '@angular/common';
import { TuiAccordionModule } from '@taiga-ui/kit';
import { Store } from '@ngxs/store';
import { TuiLetModule } from '@taiga-ui/cdk';

import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  TuiBadgedContentModule,
  TuiBadgeNotificationModule,
} from '@taiga-ui/experimental';
import {
  ItemsSelectComponent,
  ItemsSelectInputData,
} from '@shared/components/items-select/items-select.component';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import _ from 'lodash';
import { AttachmentService } from '@shared/services/attachment.service';
import { countPlacesAndSumPackages } from '@shared/utils/cargo-calculation';
import { AuthService } from '@shared/services/auth.service';
import { AuthState } from '@store/auth/auth.state';
import { PreOrder } from '../../../../types/pre-order';
import { CargoLoadingState } from '../../store/cargo-loading.state';
import { CargoLoadingService } from '../../services/cargo-loading.service';
import {
  Cargo,
  CargoLoadingAddress,
  CargoOrder,
  CargoUnloadingAddress,
} from '../../../../types/cargo';
import { OrderComponent } from './components/order/order.component';
import {
  OrderAttachmentsComponent,
  OrderAttachmentsInputData,
} from './components/order-attachments/order-attachments.component';
import { CommonStateService } from '../../services/common-state.service';
import {
  TableAvatarComponent,
  UserData,
} from './components/table-avatar/table-avatar.component';
import { ActiveProcessesComponent } from './components/active-processes/active-processes.component';

type PreparedOrder = {
  original: CargoOrder;
  computed: {
    places: string;
    packages: number;
    flowerTypeName: string;
  };
};

type CreateDialogPreOrder = PreOrder & {
  flowerTypeName: string;
  clientName: string;
  farmName: string;
  packages: number;
  places: string;
};

@Component({
  selector: 'app-orders',
  standalone: true,
  imports: [
    TuiButtonModule,
    AsyncPipe,
    TuiAccordionModule,
    TuiLetModule,
    TuiSvgModule,
    TuiBadgeNotificationModule,
    TuiBadgedContentModule,
    OrderComponent,
    TuiHintModule,
    ActiveProcessesComponent,
  ],
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersComponent implements OnInit {
  @ViewChild(OrderComponent, { read: OrderComponent })
  openedOrderComponentRef?: OrderComponent;

  snowflakeUrl = 'assets/icons/snowflake.svg';
  create$ = new Subject<void>();
  delete$ = new Subject<number>();
  downloadAllInvoices$ = new Subject<void>();
  sortByLoadingAddresses$ = new Subject<void>();
  openAttachmentsDialog$ = new Subject<{
    order: CargoOrder;
    index: number;
  }>();

  orders$ = this.store.select(CargoLoadingState.getOrders);
  preparedOrders$: Observable<PreparedOrder[]> = this.orders$.pipe(
    map(orders => {
      const flowerTypesMap = this.cargoCatalogsService.flowerTypesMap;
      return orders.map(order => {
        const { packages, places } = countPlacesAndSumPackages(order);
        return {
          original: order,
          computed: {
            packages,
            places,
            flowerTypeName:
              flowerTypesMap.get(order.flowerType ?? '')?.name ?? '',
          },
        };
      });
    }),
  );

  cargo$ = this.store.select(CargoLoadingState.getSelectedCargo);
  user$ = this.store.select(AuthState.getUser);
  users$ = this.user$.pipe(
    switchMap(user =>
      this.auth.getAllUserByDivisionId(user?.activeDivisionId ?? 0),
    ),
  );

  constructor(
    private readonly alert: AlertService,
    private readonly store: Store,
    private readonly cargoCatalogsService: CargoCatalogsService,
    protected readonly cargoLoadingService: CargoLoadingService,
    private readonly destroyRef: DestroyRef,
    private readonly dialogs: TuiDialogService,
    private readonly commonStateService: CommonStateService,
    private readonly attachmentService: AttachmentService,
    private readonly auth: AuthService,
  ) {}

  onDeleteOrders() {
    this.delete$
      .pipe(
        withLatestFrom(this.orders$, this.cargo$),
        switchMap(([index, orders, cargo]) => {
          if (!cargo) {
            return throwError(() => new Error('empty cargo'));
          }
          const cargoOrder = orders[index];
          return this.cargoLoadingService
            .deletePreOrder({
              preOrderId: cargoOrder.preOrderId ?? 0,
              id: cargo.id ?? 0,
            })
            .pipe(
              switchMap(() =>
                this.cargoLoadingService.fetchOrdersByCargoId(cargo.id),
              ),
            );
        }),
        tap(() => {
          this.alert.showSuccess();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
      )
      .subscribe();
  }

  onAddOrders() {
    this.create$
      .pipe(
        switchMap(() =>
          forkJoin({
            users: this.users$.pipe(take(1)),
            preOrders: this.cargoLoadingService.fetchAllPreOrders().pipe(
              map(preOrders => {
                const flowerTypesMap = this.cargoCatalogsService.flowerTypesMap;
                const value: CreateDialogPreOrder[] = preOrders.map(
                  preOrder => {
                    const { packages, places } =
                      countPlacesAndSumPackages(preOrder);
                    return {
                      ...preOrder,
                      clientName: preOrder.clientJson?.name ?? '',
                      farmName: preOrder.farmJson?.name ?? '',
                      flowerTypeName:
                        flowerTypesMap.get(preOrder.flowerType ?? '')?.name ??
                        '',
                      places,
                      packages,
                    };
                  },
                );
                return value;
              }),
            ),
          }),
        ),
        switchMap(({ users, preOrders }) => {
          const userDataMap = new Map(users.map(u => [u.id, u]));
          const data: ItemsSelectInputData<
            CreateDialogPreOrder & { userData: UserData }
          > = {
            headers: [
              {
                name: 'id',
                value: 'id',
              },
              {
                name: 'Клиент',
                value: 'clientName',
              },
              {
                name: 'Код',
                value: 'buyerCode',
              },
              {
                name: 'Питомник',
                value: 'farmName',
              },
              {
                name: 'Группа товаров',
                value: 'flowerTypeName',
              },
              {
                name: 'BX',
                value: 'packages',
              },
              {
                name: 'Места',
                value: 'places',
              },
              {
                name: 'Создал',
                value: 'userData',
                component: new PolymorpheusComponent(TableAvatarComponent),
              },
            ],
            identity: 'id',
            items: preOrders.map(preOrder => {
              const user = userDataMap.get(preOrder.userId);
              return {
                ...preOrder,
                userData: {
                  me: user?.id === preOrder.userId,
                  firstName: user?.firstName ?? '',
                  lastName: user?.lastName ?? '',
                },
              };
            }),
            title: 'Выбор предзаказа',
            showSearch: true,
            comparator: (search, item) => {
              return `${item.clientName}-${item.farmName}`
                .toLowerCase()
                .includes(search.toLowerCase());
            },
          };
          return this.dialogs.open<
            Array<CreateDialogPreOrder & { userName: string }>
          >(new PolymorpheusComponent(ItemsSelectComponent), {
            size: 'auto',
            data,
          });
        }),
        withLatestFrom(this.cargo$),
        switchMap(([createDialogPreOrders, cargo]) => {
          const preOrderIds: number[] = createDialogPreOrders.map(
            c => c.id ?? 0,
          );
          return this.cargoLoadingService
            .addPreOrders({
              preOrderIds,
              id: cargo?.id ?? 0,
            })
            .pipe(
              switchMap(() =>
                this.cargoLoadingService.fetchOrdersByCargoId(cargo?.id ?? 0),
              ),
            );
        }),
        tap(() => {
          this.commonStateService.refreshPreOrders$.next();
          this.alert.showSuccess();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onAttachments() {
    this.openAttachmentsDialog$
      .pipe(
        withLatestFrom(this.cargo$, this.cargoLoadingService.readonly$),
        switchMap(([{ order, index }, cargo, readonly]) => {
          const openedOrderComponentForm =
            this.openedOrderComponentRef?.orderForm.getRawValue();
          const latestOrderValue =
            openedOrderComponentForm?.id === order.id
              ? openedOrderComponentForm
              : order;

          const data: OrderAttachmentsInputData = {
            cargoId: cargo?.id ?? 0,
            order: latestOrderValue,
            canEdit: !readonly,
            canRemove: !readonly,
            canUpload: !readonly,
          };

          return this.dialogs
            .open<CargoOrder>(
              new PolymorpheusComponent(OrderAttachmentsComponent),
              {
                size: 'auto',
                data,
              },
            )
            .pipe(
              map(updatedOrder => {
                return {
                  updatedOrder,
                  order,
                  index,
                };
              }),
            );
        }),
        withLatestFrom(this.orders$),
        switchMap(([{ updatedOrder }]) => {
          return this.cargoLoadingService.updateOrder(updatedOrder).pipe(
            tap(() => {
              this.alert.showSuccess();
            }),
          );
        }),
        catchError(err => {
          this.alert.showError(err.error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onDownloadAllInvoices() {
    this.downloadAllInvoices$
      .pipe(
        withLatestFrom(this.cargo$),
        switchMap(([, cargo]) =>
          this.attachmentService.downloadByCargoId(cargo?.id ?? 0),
        ),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onSortByLoadingAddresses() {
    this.sortByLoadingAddresses$
      .pipe(
        withLatestFrom(this.orders$, this.cargo$),
        switchMap(([, orders, cargo]) => {
          if (!cargo) {
            return throwError(() => new Error('empty cargo'));
          }
          const addressOrder = new Map(
            cargo.loadingAddressesJson.map((a, i) => [a.farmName ?? '', i]),
          );
          const sorted = [...orders].sort((a, b) => {
            const ai = addressOrder.get(a.farmJson.name ?? '') ?? Infinity;
            const bi = addressOrder.get(b.farmJson.name ?? '') ?? Infinity;
            return ai - bi;
          });
          return this.cargoLoadingService.updateOrders(sorted).pipe(
            tap(() => {
              this.alert.showSuccess();
            }),
          );
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  removeAddress(
    order: CargoOrder,
    orders: CargoOrder[],
    cargo: Cargo,
  ): {
    loadingAddresses: CargoLoadingAddress[];
    unloadingAddresses: CargoUnloadingAddress[];
  } {
    const restPreOrders = orders.filter(p => p.id !== order.id);
    const restLoadingAddresses = _.groupBy(restPreOrders, preOrder => {
      return preOrder.farmJson.name ?? '';
    });
    const restUnloadingAddresses = _.groupBy(restPreOrders, preOrder => {
      return `${preOrder.clientJson.name ?? ''}-${preOrder.clientAddressWithCoordinatesJson.address ?? ''}`;
    });

    const newLoadingAddresses: CargoLoadingAddress[] =
      cargo.loadingAddressesJson.filter(address => {
        const key = address.farmName ?? '';
        return restLoadingAddresses[key] !== undefined;
      });
    const newUnloadingAddresses: CargoUnloadingAddress[] =
      cargo.unloadingAddressesJson.filter(address => {
        const key = `${address.clientName ?? ''}-${address.address ?? ''}`;
        return restUnloadingAddresses[key] !== undefined;
      });
    return {
      loadingAddresses: newLoadingAddresses,
      unloadingAddresses: newUnloadingAddresses,
    };
  }

  ngOnInit() {
    this.onAttachments();
    this.onAddOrders();
    this.onDeleteOrders();
    this.onDownloadAllInvoices();
    this.onSortByLoadingAddresses();
  }
}
